import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Compass,
  HandHeart,
  Lightbulb,
  Save,
  Sparkles,
  Sprout,
} from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import BrandHeader from "@/components/BrandHeader";
import SoftButton from "@/components/SoftButton";
import {
  getAlignment,
  getAlignmentAvenues,
  saveAlignment,
} from "@/lib/alignment";

const FIELDS = [
  {
    id: "experience",
    label: "Experience",
    eyebrow: "What do you want to look forward to?",
    prompt: "A place, feeling, relationship, adventure, season of life, ordinary pleasure…",
    icon: Sparkles,
  },
  {
    id: "growth",
    label: "Learning & Growth",
    eyebrow: "What might you want to practice, understand, or strengthen over time?",
    prompt: "Something you want to understand, practice, create, study, explore, or strengthen…",
    icon: BookOpen,
  },
  {
    id: "contribution",
    label: "Contribution",
    eyebrow: "Where would you like to be useful?",
    prompt: "A person, community, cause, problem, place, or kind of work you want to help…",
    icon: HandHeart,
  },
];

export default function StepForward() {
  const nav = useNavigate();
  const [alignment, setAlignment] = useState(() => getAlignment());
  const [showAvenues, setShowAvenues] = useState(false);

  const avenues = useMemo(
    () => getAlignmentAvenues(alignment),
    [alignment]
  );

  const update = (key, value) => {
    setAlignment((current) => ({ ...current, [key]: value }));
  };

  const save = () => {
    try {
    const next = saveAlignment(alignment);
    setAlignment(next);
    toast.success("Kept for you.", {
      description: "Alignment is a direction, not a deadline.",
    });
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  return (
    <PageContainer className="pt-7" testid="step-forward-page">
      <BrandHeader compact />

      <section className="mb-7">
        <p className="text-[10px] uppercase tracking-[0.34em] mb-2" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
          Step Forward
        </p>
        <h1 className="text-[43px] leading-[1.01] tracking-[-0.035em]" style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}>
          Move with alignment.
          <br />
          <em className="font-normal" style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}>
            No pressure.
          </em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed max-w-[390px]" style={{ color: "var(--un-text-2)" }}>
          You do not need a five-year plan. Notice what you want to experience, what you want to grow into, and where you want to contribute. Then choose one next step that feels true.
        </p>
      </section>

      <section className="rounded-[1.75rem] p-5 mb-6" style={{ background: "linear-gradient(135deg, rgba(74,45,72,0.98), rgba(109,72,102,0.95))", color: "#fffaf5" }}>
        <p className="text-[10px] uppercase tracking-[0.28em] opacity-65">Your compass</p>
        <p className="mt-2 text-[23px] leading-snug" style={{ fontFamily: "Lora, serif" }}>
          Experience · Learning · Contribution
        </p>
        <p className="mt-2 text-[12px] leading-relaxed opacity-72">
          Not goals to perform. Directions you can return to when life gets noisy.
        </p>
      </section>

      <div className="space-y-4 mb-5">
        {FIELDS.map((field, index) => {
          const Icon = field.icon;
          return (
            <motion.section
              key={field.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: index * 0.04 }}
              className="un-card p-5"
            >
              <div className="flex gap-3 items-start">
                <span className="w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center" style={{ background: "rgba(91,57,84,0.06)", color: "var(--un-plum)" }}>
                  <Icon size={17} strokeWidth={1.7} />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-[0.24em]" style={{ color: "var(--un-text-3)" }}>{field.label}</p>
                  <p className="mt-1 text-[16px] font-medium" style={{ color: "var(--un-text-1)" }}>{field.eyebrow}</p>
                </div>
              </div>
              <textarea
                value={alignment[field.id]}
                onChange={(e) => update(field.id, e.target.value)}
                placeholder={field.prompt}
                rows={3}
                className="w-full mt-4 resize-none rounded-2xl p-4 outline-none text-[14px] leading-relaxed"
                style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
              />
            </motion.section>
          );
        })}
      </div>

      <section className="rounded-[1.75rem] p-5 mb-5" style={{ background: "rgba(214,184,132,0.12)", border: "1px solid rgba(196,161,106,0.16)" }}>
        <div className="flex gap-3 items-start">
          <span className="w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center" style={{ background: "rgba(196,161,106,0.14)", color: "var(--un-ember)" }}>
            <Compass size={18} strokeWidth={1.7} />
          </span>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.24em]" style={{ color: "var(--un-text-3)" }}>Source · yours to name</p>
            <p className="mt-1 text-[16px] font-medium" style={{ color: "var(--un-text-1)" }}>What reconnects you to meaning?</p>
            <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Nature, faith, family, music, science, service, creativity, quiet, community — or something else entirely. Un-Anonymous does not define it for you.
            </p>
          </div>
        </div>
        <textarea
          value={alignment.source}
          onChange={(e) => update("source", e.target.value)}
          placeholder="What helps you remember what matters?"
          rows={2}
          className="w-full mt-4 resize-none rounded-2xl p-4 outline-none text-[14px] leading-relaxed"
          style={{ background: "rgba(255,250,245,0.75)", color: "var(--un-text-1)" }}
        />
      </section>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <SoftButton variant="secondary" size="lg" iconLeft={<Save size={16} />} onClick={save}>
          Keep this
        </SoftButton>
        <SoftButton
          variant="primary"
          size="lg"
          iconRight={<ArrowRight size={16} />}
          onClick={() => setShowAvenues((value) => !value)}
        >
          Find avenues
        </SoftButton>
      </div>

      {showAvenues && (
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-7">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Lightbulb size={16} style={{ color: "var(--un-ember)" }} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em]" style={{ color: "var(--un-text-3)" }}>Avenues, not assignments</p>
              <p className="text-[12px] mt-1" style={{ color: "var(--un-text-2)" }}>Keep what resonates. Ignore the rest.</p>
            </div>
          </div>
          <div className="space-y-3">
            {avenues.map((avenue) => (
              <div key={avenue.id} className="un-card p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: "var(--un-burgundy)" }}>{avenue.kind}</p>
                <p className="text-[16px] font-medium" style={{ color: "var(--un-text-1)" }}>{avenue.title}</p>
                <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>{avenue.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 px-2 text-[11px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
            These are general possibilities, not searched listings. Nothing you enter here is sent to an external search service.
          </p>
        </motion.section>
      )}


      <section className="grid grid-cols-2 gap-3 mb-6">
        <button
          onClick={() => nav("/peace-compass")}
          className="rounded-3xl p-4 text-left un-press"
          style={{ background: "rgba(91,57,84,0.05)", color: "var(--un-plum)" }}
        >
          <Compass size={18} />
          <p className="mt-2 text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>Check the Compass</p>
          <p className="mt-0.5 text-[11px]" style={{ color: "var(--un-text-3)" }}>Peace · Truth · Joy · Love</p>
        </button>
        <button
          onClick={() => nav("/future-funds")}
          className="rounded-3xl p-4 text-left un-press"
          style={{ background: "rgba(214,184,132,0.11)", color: "var(--un-ember)" }}
        >
          <Sprout size={18} />
          <p className="mt-2 text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>Look Forward</p>
          <p className="mt-0.5 text-[11px]" style={{ color: "var(--un-text-3)" }}>Give something ahead a little room</p>
        </button>
      </section>

      <section className="rounded-3xl px-5 py-4 mb-8 text-center" style={{ background: "rgba(91,57,84,0.035)" }}>
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          Less harm. More connection. One aligned step is enough.
        </p>
      </section>
    </PageContainer>
  );
}
